import React from "react";

import clsx from "clsx";

import { IconQuestionMarkCircle } from "@busbud/horizon";

import { useIsPoweredByWhitelabel } from "@app/context/whitelabel-context";
import { useAppContext } from "@app/helpers/hooks";
import {
  helpUrl,
  privacyUrl,
  refundsUrl,
  termsOfUseUrl
} from "@app/services/url-builders/company-pages-url-builder";

import { DownloadNativeAppButton } from "./download-native-app-button";
import { AllRightsReserved } from "../all-rights-reserved";
import { FooterLink } from "../footer-link";

const LegalLinks = () => {
  const { translator, locale } = useAppContext();
  const links = [
    {
      label: translator.t("!terms-of-use"),
      link: termsOfUseUrl(locale)
    },
    {
      label: translator.t("!privacy"),
      link: privacyUrl(locale)
    },
    {
      label: translator.t("!about.refunds.title"),
      link: refundsUrl(locale)
    }
  ];

  return (
    <ul className="flex flex-col sm:mb-0 sm:flex-row">
      {links.map((link_props, index) => (
        <li key={`legal-link-${index}`}>
          <FooterLink
            size={{ initial: "md", sm: "sm" }}
            className={clsx("no-underline", {
              ["after:ml-100 after:mr-100 after:hidden after:text-color-tertiary after:content-['•'] sm:after:inline"]:
                index < links.length - 1
            })}
            {...link_props}
          />
        </li>
      ))}
    </ul>
  );
};

const HelpLinks = () => {
  const { translator, locale } = useAppContext();
  const is_powered_by = useIsPoweredByWhitelabel();

  return (
    <FooterLink
      linkClassName={`${is_powered_by ? "help-center-redirect-modal-link" : ""}`}
      className="mb-200 md:mb-0"
      link={helpUrl(locale)}
      iconStart={
        <IconQuestionMarkCircle size="sm" className="text-icon-color-primary" />
      }
      label={translator.t("!layout.footer.help")}
    />
  );
};

type Variant = "default" | "app-links";
interface Props {
  variant?: Variant;
}

const FooterBaseComponent: React.FC<Props> = ({ variant = "default" }) => {
  const { features } = useAppContext();

  const should_favor_app_links = variant === "app-links";

  return (
    <div className="flex flex-1 flex-col justify-between sm:flex-col-reverse sm:items-center md:flex-row">
      <div className="flex flex-[2_2_0%] flex-col xl:flex-row xl:items-center">
        <div className="mb-200 flex-1 sm:mb-0 sm:text-center md:text-left">
          <AllRightsReserved company_name="Busbud Inc." />
        </div>
        <div className="flex flex-1 xl:justify-center">
          <LegalLinks />
        </div>
      </div>
      <div
        className={clsx("hidden flex-1 sm:flex md:justify-end", {
          ["sm:hidden"]: should_favor_app_links
        })}
      >
        <HelpLinks />
      </div>
      {!features.SHOULD_HIDE_MOBILE_APPS_PROMOTION && (
        <ul
          className={clsx("flex", {
            ["flex flex-1 justify-start gap-200 sm:mb-200 md:mb-0 md:justify-end"]:
              should_favor_app_links,
            ["sm:hidden"]: !should_favor_app_links
          })}
        >
          <li>
            <DownloadNativeAppButton platform="ios" />
          </li>
          <li>
            <DownloadNativeAppButton platform="android" />
          </li>
        </ul>
      )}
    </div>
  );
};

export const FooterBase = React.memo(FooterBaseComponent);
